import { Fragment, useState } from "react";

import { IconButton } from "@/components/Button/IconButton";
import { CheckCircleIcon } from "@/components/Icons/CheckCircleIcon";
import { Hourglass01Icon } from "@/components/Icons/Hourglass01Icon";
import { Loading01Icon } from "@/components/Icons/Loading01Icon";
import { Trash01Icon } from "@/components/Icons/Trash01Icon";
import { XCircleIcon } from "@/components/Icons/XCircleIcon";
import { buttonVariants } from "@/design-system/Button";
import { Input } from "@/design-system/Input";
import { Select } from "@/design-system/Select";
import { Tooltip } from "@/design-system/Tooltip";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";
import { RuntimeGridColumns } from "@/ui-adapters/RuntimeGrid";

import type { Column, Data } from "./parser";
import type { Validation } from "./validation";

interface Props<TColumns extends readonly Column<any>[]> {
  data: Data<TColumns>;
  columns: TColumns;
  dataState: Validation;
  onUpdateValue: (row: number, column: string, value: string) => void;
  onRemoveRow: (row: number) => void;
  disabled: boolean;
  "data-testid"?: string;
}
export function DataTable<T extends readonly Column<any>[]>({
  data,
  columns,
  dataState,
  onUpdateValue,
  onRemoveRow,
  disabled,
  "data-testid": dataTestId,
}: Props<T>): React.ReactNode {
  const { t } = useTranslation();
  const [editingColumn, setEditingColumn] = useState<{
    row: number;
    column: string;
  }>();

  return (
    <RuntimeGridColumns template={`auto repeat(${columns.length}, minmax(min-content, auto)) auto`}>
      {(runtimeGridAttributes) => (
        <div
          {...runtimeGridAttributes}
          className="grid w-full items-center gap-space-xs overflow-auto"
          data-testid={dataTestId}
        >
          <span />
          {columns.map((x) => (
            <span key={x.name} className="-mt-px -ml-px px-field-inline py-space-xs text-body-emphasis">
              {x.name}
            </span>
          ))}
          <span />
          {data.map((row, y) => (
            <Fragment key={row.id}>
              {row.uploadStatus === "NONE" && <span />}
              {row.uploadStatus === "QUEUED" && (
                <span
                  role="img"
                  aria-label={t("component.data-table.upload-status.waiting")}
                  className="m-space-sm flex items-center justify-center text-text-tertiary"
                >
                  <Hourglass01Icon />
                </span>
              )}
              {row.uploadStatus === "UPLOADING" && (
                <span
                  role="img"
                  aria-label={t("component.data-table.upload-status.uploading")}
                  className="m-space-sm flex animate-spin items-center justify-center text-text-secondary"
                >
                  <Loading01Icon />
                </span>
              )}
              {row.uploadStatus === "SUCCESS" && (
                <span
                  role="img"
                  aria-label={t("component.data-table.upload-status.success")}
                  className="m-space-sm flex items-center justify-center text-text-success"
                >
                  <CheckCircleIcon />
                </span>
              )}
              {row.uploadStatus === "ERROR" && (
                <span
                  role="img"
                  aria-label={t("component.data-table.upload-status.error")}
                  className="m-space-sm flex items-center justify-center text-text-danger-secondary"
                >
                  <XCircleIcon />
                </span>
              )}
              {columns.map((c) => {
                const value = row[c.name as Exclude<keyof typeof row, "id" | "uploadStatus">];
                const warning = dataState.getWarnings(y, c.name);
                const invalid = dataState.getInvalidError(y, c.name);
                const missing = dataState.getMissingError(y, c.name);
                const isEditing = editingColumn?.row === y && editingColumn.column === c.name;
                const rowDisabled = row.uploadStatus === "SUCCESS" || disabled;

                let cellClassName: string;
                if (row.uploadStatus === "SUCCESS") {
                  cellClassName = "";
                } else if (invalid || missing) {
                  cellClassName =
                    "bg-background-danger-secondary ring-1 ring-border-danger hocus:bg-background-danger-secondary/50";
                } else if (warning) {
                  cellClassName =
                    "bg-background-warning-secondary ring-1 ring-border-warning hocus:bg-background-warning-secondary/50";
                } else {
                  cellClassName = "hocus:bg-background-secondary";
                }

                return (
                  <span className="relative" key={c.name}>
                    {isEditing ? (
                      <div className="absolute z-raised m-auto -mt-space-xs -ml-space-xs w-full min-w-control-narrow">
                        {c.rules && "oneOf" in c.rules ? (
                          <Select
                            autoFocus
                            items={c.rules.oneOf.values(row)}
                            selected={value}
                            keySelector={(x) => x}
                            renderOption={(x) => (c.render ? c.render(x) : x)}
                            onChange={(newValue) => {
                              onUpdateValue(y, c.name, newValue);
                              setEditingColumn(undefined);
                            }}
                            onBlur={() => setEditingColumn(undefined)}
                          />
                        ) : (
                          <Input
                            autoFocus
                            defaultValue={value}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                onUpdateValue(y, c.name, event.currentTarget.value?.trim());
                                setEditingColumn(undefined);
                              }
                              if (event.key === "Escape") {
                                setEditingColumn(undefined);
                              }
                            }}
                            onBlur={(event) => {
                              onUpdateValue(y, c.name, event.currentTarget.value?.trim());
                              setEditingColumn(undefined);
                            }}
                          />
                        )}
                      </div>
                    ) : null}
                    <Tooltip tooltip={row.uploadStatus === "SUCCESS" ? undefined : invalid || missing || warning}>
                      <button
                        type="button"
                        className={twResolve(
                          buttonVariants({ variant: "ghostSecondary", size: "sm", fullWidth: true, type: "button" }),
                          "-mt-px h-auto justify-start rounded-none text-left font-normal ring-inset active:ring-2 active:ring-border-tertiary disabled:pointer-events-none disabled:bg-background-secondary disabled:text-text-disabled",
                          value ? "text-text-primary" : "text-text-primary/40",
                          cellClassName,
                        )}
                        disabled={rowDisabled}
                        onClick={() =>
                          setEditingColumn({
                            column: c.name,
                            row: y,
                          })
                        }
                      >
                        <span className="w-full px-field-inline py-space-xs">
                          {(c.render && value ? c.render(value) : value) || "-"}
                        </span>
                      </button>
                    </Tooltip>
                  </span>
                );
              })}
              <IconButton
                title={t("page.user-bulk-upload.delete-row")}
                disabled={disabled}
                onClick={() => onRemoveRow(y)}
                variant="filled"
                tone="error"
                size="sm"
                icon={<Trash01Icon />}
              />
            </Fragment>
          ))}
        </div>
      )}
    </RuntimeGridColumns>
  );
}
